import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, Download, CheckCircle2, XCircle } from "lucide-react";
import { useTableSort } from "@/lib/tableSort";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export const MerkezHistory = () => {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const mhT = useTableSort([]);

  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    api.get("/yetki/merkez-history", { params: term ? { q: term } : {} })
      .then(({ data }) => setData(data))
      .catch(() => setData({ rows: [], total: 0, approved: 0, rejected: 0 }));
  }, [term]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/yetki/merkez-history/export", {
        params: { format: "xlsx", ...(term ? { q: term } : {}) }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MERKEZ BELGE ONAY GEÇMİŞİ.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally { setBusy(false); }
  };

  if (!data) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-3" data-testid="merkez-history">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm" data-testid="merkez-history-summary">
          <span className="font-bold text-emerald-600">{data.approved}</span> onaylandı ·{" "}
          <span className="font-bold text-red-600">{data.rejected}</span> reddedildi
        </p>
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 h-9 w-[230px]" placeholder="Firma veya kişi ara..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="merkez-history-search" />
        </div>
        <Button variant="outline" size="sm" className="h-9" disabled={busy || !data.rows.length}
          onClick={download} data-testid="merkez-history-export">
          <Download className="h-4 w-4 mr-1.5" />Excel
        </Button>
      </div>

      {data.rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="merkez-history-empty">
          Henüz onay/ret kaydı yok.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="merkez-history-table">
            <thead className="sticky top-0 bg-muted/90 backdrop-blur">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <mhT.Th className="px-3 py-2 font-medium" sortKey="company" text get={(r) => r.company_name}>Firma</mhT.Th>
                <mhT.Th className="px-3 py-2 font-medium" sortKey="committee" lowBest get={(r) => r.committee_no}>Komite</mhT.Th>
                <mhT.Th className="px-3 py-2 font-medium" sortKey="decision" text get={(r) => r.decision}>Karar</mhT.Th>
                <mhT.Th className="px-3 py-2 font-medium" sortKey="actor" text get={(r) => r.actor}>İşlemi Yapan</mhT.Th>
                <mhT.Th className="px-3 py-2 font-medium" sortKey="date" get={(r) => r.timestamp}>Tarih</mhT.Th>
                <mhT.Th className="px-3 py-2 font-medium" sortKey="reason" text get={(r) => r.reason}>Ret Sebebi</mhT.Th>
              </tr>
            </thead>
            <tbody>
              {mhT.applySort(data.rows).map((r) => (
                <tr key={r.id} className="border-t border-border/60" data-testid={`merkez-history-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.company_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-muted-foreground">#{r.committee_no}</span> {r.committee_name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.decision === "approved" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                        <CheckCircle2 className="h-3.5 w-3.5" />Onaylandı
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                        <XCircle className="h-3.5 w-3.5" />Reddedildi
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.actor}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmt(r.timestamp)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MerkezHistory;
